import { createContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthContextType,
  AuthState,
} from '@/types/auth.types';
import { TokenService } from '@/services/auth/TokenService';
import { clearCompanyScope } from '@/services/companyScope';
import { authAPI, ApiError } from '@/services/api/auth.api';
import { ensurePushForCurrentUser } from '@/platform/push';
import { reportDevice } from '@/platform/deviceReport';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (
    error instanceof ApiError &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'error' in error.data
  ) {
    return String((error.data as { error: string }).error);
  }
  return error instanceof Error ? error.message : fallback;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Initialize auth: with token, fetch fresh user from backend (keeps level, etc. in sync)
  useEffect(() => {
    const initializeAuth = async () => {
      // En iOS/Android, recupera la sesión desde el almacenamiento nativo
      // (Preferences) por si el WebView purgó el localStorage. No-op en web.
      await TokenService.hydrateFromNative();

      const token = TokenService.getToken();
      const storedUser = TokenService.getUser<User>();

      if (!token || !storedUser) {
        setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
        return;
      }

      // Hay sesión guardada → restauramos YA con el usuario cacheado, sin
      // esperar al backend (arranque más rápido) y refrescamos en segundo
      // plano. La sesión NO se cierra por fallos transitorios (red, 5xx, cold
      // start del Worker) — esa era la causa real de los deslogueos constantes.
      setState({ user: storedUser, isAuthenticated: true, isLoading: false, error: null });
      // Re-vincula la push de este dispositivo a la sesión restaurada.
      void ensurePushForCurrentUser();

      try {
        const user = await authAPI.getMe();
        TokenService.setUser(user, TokenService.isAuthenticated());
        setState({ user, isAuthenticated: true, isLoading: false, error: null });
      } catch (error) {
        // SOLO cerramos sesión si el servidor RECHAZA el token de verdad
        // (401/403): ahí el token está muerto (secreto rotado, cuenta
        // desactivada) y mantener la sesión dejaría la app rota sin salida —
        // el alumno no podría ni cargar datos, así que lo llevamos limpio a
        // login. Cualquier otro error mantiene la sesión con el usuario
        // cacheado.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          TokenService.clearAuth();
          setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
        }
      }
    };

    void initializeAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<User> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authAPI.login(credentials);
      TokenService.setToken(response.token);
      TokenService.setUser(response.user);
      setState({ user: response.user, isAuthenticated: true, isLoading: false, error: null });
      // Rebind-on-login: este dispositivo pasa a pertenecer a la cuenta que
      // acaba de entrar (clave para el escenario multi-cuenta).
      void ensurePushForCurrentUser();
      // Devolvemos el usuario para que el caller (LoginPage) decida el destino
      // según rol + dispositivo (regla del usuario dual alumno + admin).
      return response.user;
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Unable to log in. Please check your connection and try again.'
      );
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authAPI.register(data);
      TokenService.setToken(response.token, false);
      TokenService.setUser(response.user, false);
      setState({ user: response.user, isAuthenticated: true, isLoading: false, error: null });
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Unable to complete registration. Please try again later.'
      );
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const acceptInvitation = useCallback(
    async (data: { token: string; name: string; password: string }): Promise<User> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await authAPI.acceptInvitation(data);
        TokenService.setToken(response.token, false);
        TokenService.setUser(response.user, false);
        setState({ user: response.user, isAuthenticated: true, isLoading: false, error: null });
        // Vincula la push de este dispositivo a la cuenta recién creada.
        void ensurePushForCurrentUser();
        return response.user;
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Unable to activate your account. Please try again later.'
        );
        setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
        throw new Error(errorMessage);
      }
    },
    []
  );

  const logout = useCallback((): void => {
    TokenService.clearAuth();
    clearCompanyScope();
    setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
  }, []);

  const updateUser = useCallback((updatedUser: Partial<User>): void => {
    setState((prev) => {
      if (!prev.user) return prev;
      const newUser = { ...prev.user, ...updatedUser };
      TokenService.setUser(newUser, TokenService.isAuthenticated());
      return { ...prev, user: newUser };
    });
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({ ...state, login, register, acceptInvitation, logout, updateUser }),
    [state, login, register, acceptInvitation, logout, updateUser]
  );

  // Reporta el dispositivo (plataforma + versión) al autenticar, para verlo en el backoffice.
  useEffect(() => {
    if (state.user?.id) void reportDevice(state.user.id);
  }, [state.user?.id]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
